import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { filter, first, startWith } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import {
  CCAuthService,
  CCPicklistService,
  GoogleOAuthService,
  getIntendedRoute,
  clearIntendedRoute,
} from '@maids/cc-erp-services';
import { CacheService } from '../../services/cache.service';
import { OAuthAuthorizationService } from '../../services/oauth-authorization.service';
import { environment } from 'src/environments/environment';
import { assets } from '@maids/cc-lib/utils';
import { trigger, style, animate, transition } from '@angular/animations';

import { CCInputComponent } from '@maids/cc-lib/input';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1000, style({ opacity: 1 })),
            ]),
            transition(':leave', [animate(1000, style({ opacity: 0 }))]),
        ]),
    ],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  quotes: string[] = [];
  currentIndex: number = 0;
  currentQuote: string = '';
  showQuoteTime: number = 12000; // 12s

  showPassword: boolean = false;
  smileyDecimal: string = '&#128578;';
  wavingDecimal: string = '&#128075;';
  quoteAnimationState = '';
  isLandscape: boolean = false;
  isResponsiveLandscape: boolean = false;

  googleConfig = {
    scope: 'openid email',
    redirectUri: environment.googleRedirectUri,
    state: {
      app: 'new-erp',
      host: window.location.origin,
    },
  };

  @ViewChild('usernameInput') usernameInput!: CCInputComponent;
  @ViewChild('passwordInput') passwordInput!: CCInputComponent;

  constructor(
    private _auth: CCAuthService,
    private _router: Router, // @Inject(CC_INITIAL_URL_QUERY_PARAMS) private initParams: any,
    private _picklistService: CCPicklistService,
    private _cdr: ChangeDetectorRef,
    private _googleOAuth: GoogleOAuthService,
    @Inject(DOCUMENT) private document: Document,
    private _route: ActivatedRoute,
    private _cacheService: CacheService,
    private _oauthAuthorization: OAuthAuthorizationService
  ) {}
  private readonly _currentNavigation = this._router.getCurrentNavigation();
  private readonly _subscriptions = new Subscription();

  ngOnInit(): void {
    this._oauthAuthorization.captureFromQueryParams(
      this._route.snapshot.queryParams
    );
    this.getQuotes();
    this.checkScreenOrientation();
    // Check if this is a Google OAuth callback
    this.handleGoogleOAuthCallback();
  }

  iterateOverQuotes() {
    if (!this.quotes.length) return;
    this._subscriptions.add(
      interval(this.showQuoteTime)
        .pipe(startWith(0))
        .subscribe(() => {
          this.currentIndex = (this.currentIndex + 1) % this.quotes.length;
          this.currentQuote = '';
          this._cdr.detectChanges();
          setTimeout(() => {
            this.currentQuote = this.quotes[this.currentIndex];
            this._cdr.detectChanges();
          }, 2000);
        })
    );
  }

  authenticate(username: string, password: string) {
    this.clearAllCookies();
    this._cacheService.clearERPMenuCache();
    this._subscriptions.add(
      this._auth
        .authenticate(username, password)
        .pipe(first())
        .subscribe(
          (user: any) => {
            if (user) {
              // Check if user is a freedom user
              this._subscriptions.add(
                this._auth.loggedUser$
                  .pipe(first())
                  .subscribe((loggedInUser: any) => {
                    if (this._oauthAuthorization.hasPendingAuthorization()) {
                      this._router.navigate(['/oauth/consent']);
                      return;
                    }

                    // if (loggedInUser?.freedomUser === true) {
                    //   // Redirect to freedom operator dashboard
                    //   window.location.href = '/freedom-operator/v2/dashboard';
                    // } else {
                    //   // Normal redirect flow
                    //   const redirect =
                    //     this._currentNavigation?.extras.state?.['redirectTo'];
                    //   this._router.navigate([redirect ?? '/']);
                    // }
                    const intendedRoute = getIntendedRoute();
                    if (intendedRoute) {
                      console.log(
                        'Redirecting to intended route:',
                        intendedRoute
                      );
                      // Clear the stored route
                      clearIntendedRoute();
                      // Navigate to the intended route
                      window.location.href = intendedRoute;
                    } else {
                      // No stored route, go to default page
                      this._router.navigate(['/']);
                    }
                  })
              );
            } else {
              // If authentication fails, clear cookies
              this.clearAllCookies();
            }
          },
          (error) => {
            // Handle error, and clear cookies if authentication fails
            this.clearAllCookies();
          }
        )
    );
  }

  changePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  imageUrl(path: string) {
    return environment.production && !environment.newErp
      ? assets.getBasePath() + `new-theme-pages/assets/images/${path}`
      : assets.getBasePath() + `assets/images/${path}`;
  }

  getQuotes() {
    this._picklistService
      .getPicklist({
        code: 'login_quotes',
      })
      .subscribe((res) => {
        this.quotes = res.map(
          (item: { label: string; id: number }) => item.label
        );
        if (this.quotes.length) {
          const maxStartIndex = Math.min(15, this.quotes.length - 1);
          const startIndex = Math.floor(Math.random() * (maxStartIndex + 1));
          // Seed so the first interval tick shows `startIndex` (keeps current behavior).
          this.currentIndex =
            (startIndex - 1 + this.quotes.length) % this.quotes.length;
        }
        this.iterateOverQuotes();
      });
  }

  clearAllCookies() {
    const cookies = this.document.cookie.split(';');
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      this.document.cookie =
        name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }

  showMessage: boolean = false;

  toggleMessage() {
    this.showMessage = !this.showMessage;
  }

  clickOnUsernameInput() {
    this.usernameInput.inputElement?.nativeElement.click();
  }

  clickOnPasswordInput() {
    this.passwordInput.inputElement?.nativeElement.click();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenOrientation();
  }

  private checkScreenOrientation() {
    this.isLandscape =
      window.innerWidth > window.innerHeight && window.innerWidth < 880;
    this.isResponsiveLandscape =
      window.innerWidth > window.innerHeight &&
      window.innerWidth > 880 &&
      window.innerWidth < 1400;
  }

  /**
   * Start Google OAuth authentication
   */
  startGoogleAuth(): void {
    this.onGoogleAuthStart();
    this._googleOAuth.startAuthorizationCodeFlow(this.googleConfig);
  }
  /**
   * Handle Google OAuth callback on page load
   */
  private handleGoogleOAuthCallback(): void {
    this._subscriptions.add(
      this._googleOAuth.handleOAuthCallback().subscribe({
        next: (user) => {
          if (user) {
            this.onGoogleAuthSuccess(user);
          }
        },
        error: (error) => this.onGoogleAuthError(error),
      })
    );
  }

  /**
   * Handle successful Google authentication
   */
  async onGoogleAuthSuccess(user: any): Promise<void> {
    this._subscriptions.add(
      this._auth.loggedUser$.pipe(first()).subscribe((loggedInUser: any) => {
        if (this._oauthAuthorization.hasPendingAuthorization()) {
          this._router.navigate(['/oauth/consent']);
          return;
        }

        if (loggedInUser?.freedomUser === true) {
          window.location.href = '/freedom-operator/v2/dashboard';
          return;
        }

        const intendedRoute = getIntendedRoute();
        if (intendedRoute) {
          clearIntendedRoute();

          // Prefer SPA navigation if intendedRoute is within the same Angular app:
          window.location.href = intendedRoute;

          // If you truly need a full reload / micro-frontend jump, keep href instead.
          // window.location.href = intendedRoute;
        } else {
          this._router.navigateByUrl('/');
        }
      })
    );
  }

  /**
   * Handle Google authentication error
   */
  onGoogleAuthError(error: any): void {
    console.error('Google authentication error:', error);

    // Handle different error types with user-friendly messages
    if (error.includes?.('popup_blocked')) {
      // Show message about popup being blocked
      console.warn('Popup was blocked for Google sign-in');
    } else if (error.includes?.('access_denied')) {
      // Show message about user cancelling
      console.warn('Google sign-in was cancelled');
    } else {
      // Generic error message
      console.warn('Google authentication failed. Please try again.');
    }

    // Clear any cookies on auth failure
    this.clearAllCookies();
  }

  /**
   * Handle Google authentication start
   */
  onGoogleAuthStart(): void {
    // Optional: Show loading state
    console.log('Google authentication started');
  }
}
